import { ReactElement, ReactNode, useMemo } from 'react'

import { useGetCryptoCurrencyNameById } from 'components/features/rewards/rewardsPane/hooks/useGetCryptoCurrencyNameById'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { OrderSuccessResultWithMeta } from 'components/features/rewards/rewardsPane/types/orderResult'
import {
  FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE,
  FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE,
  FLOATING_PANE_AUTO_INVESTMENTS_MODE,
} from 'components/features/rewards/rewardsPane/constants/paneState'
import { CryptoRewardsPaneMode } from 'components/features/rewards/rewardsPane/types/paneState'

import styles from 'components/features/rewards/rewardsPane/components/CryptoRewardsOrderSuccessResultWarning/CryptoRewardsOrderSuccessResultWarning.styl'


type CryptoRewardsOrderSuccessResultWarningProps<M extends CryptoRewardsPaneMode> = {
  renderMode: M
  orderSuccessResult: OrderSuccessResultWithMeta<CryptoRewardsPaneMode>
}

export function CryptoRewardsOrderSuccessResultWarning<M extends CryptoRewardsPaneMode> ({
  renderMode,
  orderSuccessResult,
}: CryptoRewardsOrderSuccessResultWarningProps<M>): ReactElement {
  const { currencyId } = orderSuccessResult
  const currencyName = useGetCryptoCurrencyNameById(currencyId)

  const info = useMemo<ReactNode>(() => {
    switch (renderMode) {
      case FLOATING_PANE_AUTO_INVESTMENTS_MODE:
        return <>We’ll invest your rewards in <span className={ styles.bold }>{ currencyName }</span> when you earn $10 or more.</>
      case FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE:
        return <>We used your rewards to place an order for</>
      case FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE:
        return 'We submitted your sale of'
      default:
        return 'Unknown result'
    }
  }, [currencyName, renderMode])
  const bolderInfo = useMemo<ReactNode>(() => {
    if (orderSuccessResult.mode !== renderMode) {
      return null
    }
    switch (orderSuccessResult.mode) {
      case FLOATING_PANE_AUTO_INVESTMENTS_MODE:
        return null
      case FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE: {
        const { amountUSD } = orderSuccessResult

        return `${ formatUSCurrency(amountUSD, 'penny') } of ${ currencyName }`
      }
      case FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE: {
        const { amountUSD } = orderSuccessResult

        return <><br />{ currencyName } for { formatUSCurrency(amountUSD, 'penny') }</>
      }
      default:
        return ''
    }
  }, [renderMode, currencyName, orderSuccessResult])

  return (
    <div className={ styles.overview }>
      {info} <span className={ styles.bold }>{bolderInfo}</span>
    </div>
  )
}
